import React, { useEffect, useRef, useState } from "react";

// Mirrors the DataGrid horizontal scrollbar while its native scrollbar is below the viewport.
// The grid remains the only owner of horizontal scroll state.
export default function StickyListingScrollbar({ gridRef }) {
  const barRef = useRef(null);
  const syncingRef = useRef(false);
  const [layout, setLayout] = useState({ visible: false, left: 0, width: 0, contentWidth: 0 });

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    let scroller;
    let observer;
    let frame = 0;

    const update = () => {
      scroller = grid.querySelector(".MuiDataGrid-virtualScroller");
      if (!scroller) return;
      const rect = scroller.getBoundingClientRect();
      const gridRect = grid.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const overflows = scroller.scrollWidth > scroller.clientWidth + 1;
      // Hide when the native horizontal scrollbar is visible or the grid is out of view.
      const nativeBarVisible = rect.bottom <= viewportHeight;
      const visible = overflows && rect.top < viewportHeight && gridRect.bottom > viewportHeight && !nativeBarVisible;
      setLayout((prev) => {
        const next = { visible, left: Math.max(0, rect.left), width: Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(0, rect.left)), contentWidth: scroller.scrollWidth };
        return Object.keys(next).every((key) => prev[key] === next[key]) ? prev : next;
      });
      if (barRef.current && !syncingRef.current && barRef.current.scrollLeft !== scroller.scrollLeft) {
        barRef.current.scrollLeft = scroller.scrollLeft;
      }
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const onGridScroll = () => {
      if (barRef.current && scroller && !syncingRef.current) barRef.current.scrollLeft = scroller.scrollLeft;
    };
    const attach = () => {
      scroller = grid.querySelector(".MuiDataGrid-virtualScroller");
      if (scroller) scroller.addEventListener("scroll", onGridScroll, { passive: true });
      schedule();
    };
    attach();
    observer = new ResizeObserver(schedule);
    observer.observe(grid);
    if (scroller) observer.observe(scroller);
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      if (scroller) scroller.removeEventListener("scroll", onGridScroll);
      observer.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [gridRef]);

  const onScroll = (event) => {
    const scroller = gridRef.current?.querySelector(".MuiDataGrid-virtualScroller");
    if (!scroller) return;
    syncingRef.current = true;
    scroller.scrollLeft = event.currentTarget.scrollLeft;
    syncingRef.current = false;
  };

  return (
    <div
      ref={barRef}
      onScroll={onScroll}
      aria-label="Listings horizontal scrollbar"
      style={{
        position: "fixed", bottom: 0, left: layout.left, width: layout.width,
        overflowX: "auto", overflowY: "hidden", height: 17,
        zIndex: 1200, background: "#f3f5f8", borderTop: "1px solid #c9d1da",
        display: layout.visible ? "block" : "none",
      }}
    >
      <div style={{ width: layout.contentWidth, height: 1 }} />
    </div>
  );
}
